import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import cn from 'classnames';
import { SearchField } from '@p81-common/ui';

import DropDownMenu from '../Main/parts/DropDownMenu/DropDownMenu';
import Account from '../Main/parts/Account/Account';
import {
  Icon,
  Loading,
  Heading,
  Text,
  CompanyStatus,
  Button,
  TableTitle,
  Member,
  SortingIcon,
  ModalWindowMobile,
  EmptyState
} from 'components/common/Elements/Elements';

import { postRequest, getRequest } from 'utils/requests';
import { getFullName, getInitials, detectOS } from 'utils/common';
import { useRegionalLink } from 'hooks/useRegionalLink';

import LogoText from 'assets/svg/jarvis.svg';

import classes from './Team.module.scss';

const Team = () => {
  const unmount = useRef(false);
  const params = useParams();
  const generateRegionalPath = useRegionalLink();

  const [loader, setLoader] = useState(true);
  const [data, setData] = useState({});
  const [dataCompany, setDataCompany] = useState({});
  const [search, setSearch] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [teamData, setTeamData] = useState([]);
  const [dataForTable, setDataForTable] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModalRole, setIsOpenModalRole] = useState(false);
  const [selectMember, setSelectMember] = useState({});
  const [filterRoles, setFilterRoles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  const [direction, setDirection] = useState(-1);

  const os = detectOS();

  useEffect(() => {
    const checkedField = 'firstName';
    let arr = [];
    if (teamData.data && teamData.data.length > 0) {
      arr = [
        ...teamData.data.sort((a, b) => {
          return direction > 0
            ? a[checkedField] < b[checkedField]
              ? 1
              : -1
            : b[checkedField] < a[checkedField]
            ? 1
            : -1;
        })
      ];
    }
    if (filterRoles.length > 0) {
      arr = arr.filter(item => filterRoles.includes(item.role));
    }
    setDataForTable([...arr]);
  }, [teamData, direction, filterRoles]);

  useEffect(() => {
    let cancelled = false;
    const linkForRequest = `/mob/users/list?search=${
      search ? search : '*'
    }&customerId=${params.id}`;
    getRequest(linkForRequest)
      .then(res => {
        if (!cancelled) {
          if (res.body.data.length > 0) {
            const listRoles = roles;
            res.body.data.forEach(element => {
              if (!listRoles.includes(element.role)) {
                listRoles.push(element.role);
              }
            });
            setRoles(listRoles);
            setSearchCompany('');
          }
          setTeamData(res.body);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
        }
      });
    return () => (cancelled = true);
    // eslint-disable-next-line
  }, [search, params.id]);

  useEffect(() => {
    setSearch('');
  }, [params.id]);

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  const handleSearchCompany = e => {
    setSearchCompany(e.target.value);
  };

  const searchClear = () => {
    setSearch('');
  };

  const searchCompanyClear = () => {
    setSearchCompany('');
  };

  useEffect(() => {
    if (searchCompany) {
      requestForSearch();
    }
    // eslint-disable-next-line
  }, [searchCompany]);

  useEffect(() => {
    let cancelled = false;
    postRequest('/customer/header', {
      customerId: params.id
    })
      .then(res => {
        if (!cancelled) {
          setData(res.body);
          setLoader(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
          setLoader(false);
        }
      });
    return () => (cancelled = true);
  }, [params.id, dataCompany]);

  const requestForSearch = () => {
    const linkForRequest = `/mob/customers/list?search=${searchCompany}`;

    getRequest(linkForRequest)
      .then(res => {
        if (!unmount.current) {
          setDataCompany(res.body.data);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          console.log(err);
        }
      });
  };

  const linkOpen = link => {
    return window.open(link, '_blank', 'noopener,noreferrer');
  };

  const createAgentLink = implicitToken => {
    let link;

    switch (os) {
      case 'MacOS':
        link = `perimeter81://perimeter81.com/macos/callback?id_token=${implicitToken}&tenant_id=${params.id}&tokenType=implicitToken`;
        break;
      case 'Windows':
        link = `perimeter81://perimeter81.com/callback?id_token=${implicitToken}&tenant_id=${params.id}&tokenType=implicitToken`;
        break;
      case 'Linux':
        link = `com.perimeter81.linux://callback?id_token=${implicitToken}&tenant_id=${params.id}&tokenType=implicitToken`;
        break;
    }
    return link;
  };

  const createMember = user => {
    return {
      fullName: getFullName(user.firstName, user.lastName),
      description: user.email,
      initials: getInitials(user.firstName, user.lastName),
      initialsColor: user.initialscolor,
      icon: user.icon === '-' || user.icon === 'NA' ? null : user.icon
    };
  };

  const handleSortable = () => {
    setDirection(direction * -1);
  };

  const handleRole = () => {
    setIsOpenModalRole(true);
  };

  const handleLogin = user => {
    setSelectMember(user);
    setIsOpenModal(true);
  };

  const handlePlatform = email => {
    const REQ_BODY = {
      customerId: params.id,
      userEmail: email
    };
    postRequest('/customer/platform/team/supportlink', REQ_BODY)
      .then(res => {
        res.body.link && linkOpen(res.body.link);
      })
      .catch(err => {
        console.log(err);
      });
  };

  const handleAgent = email => {
    const REQ_BODY = {
      customerId: params.id,
      userEmail: email,
      rawToken: true
    };
    postRequest('/customer/platform/team/supportlink', REQ_BODY)
      .then(res => {
        res.body.implicitToken &&
          linkOpen(createAgentLink(res.body.implicitToken));
      })
      .catch(err => {
        console.log(err);
      });
  };

  const changeRole = role => {
    if (!filterRoles.includes(role)) {
      setFilterRoles([...filterRoles, role]);
    } else {
      setFilterRoles([...filterRoles.filter(item => item !== role)]);
    }
  };

  const isCheckedRole = role => {
    return filterRoles.includes(role);
  };

  return (
    <>
      <ModalWindowMobile isOpen={isOpenModal} setIsOpen={setIsOpenModal}>
        <div>
          <Member member={createMember(selectMember)} />
          <div className={classes.wrapBtn}>
            <Button
              onClick={() => handleAgent(selectMember.email)}
              className={classes.btnLoginS}
              platform="platform"
            >
              Login as Agent
            </Button>
          </div>
          <div>
            <Button
              onClick={() => handlePlatform(selectMember.email)}
              className={classes.btnLoginS}
              platform="platform"
            >
              Login as Platform
            </Button>
          </div>
        </div>
      </ModalWindowMobile>
      <ModalWindowMobile
        isOpen={isOpenModalRole}
        setIsOpen={setIsOpenModalRole}
      >
        <div>
          <Heading size={2}>Sort by Role</Heading>
          <ul className={classes.wrapBtn}>
            {roles.map(role => (
              <li key={role}>
                <Button
                  className={classes.option}
                  onClick={() => changeRole(role)}
                >
                  <div
                    className={cn(
                      classes.checkBox,
                      isCheckedRole(role) && classes.checkBox_selected
                    )}
                  >
                    {isCheckedRole(role) && <div className={classes.arrow} />}
                  </div>
                  {role}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </ModalWindowMobile>
      <div className={classes.header}>
        {showSearch ? (
          <div className={classes.wrapSearch}>
            <SearchField
              placeholder={'Search tenant'}
              value={searchCompany}
              handleChangeCallback={handleSearchCompany}
              handleClear={searchCompanyClear}
              className={classes.search}
            />
            {dataCompany.length > 0 && searchCompany && (
              <DropDownMenu className={classes.companyList}>
                <ul>
                  {dataCompany.map((tenant, index) => (
                    <li key={index} className={classes.list}>
                      <Account company={tenant} />
                    </li>
                  ))}
                </ul>
              </DropDownMenu>
            )}
          </div>
        ) : (
          <>
            <NavLink
              to={generateRegionalPath('/mobile/main')}
              className={classes.link}
            >
              <Icon
                size="35px"
                icon={'arrow'}
                color={'white'}
                style={{
                  transform: `rotate(${90}deg)`
                }}
              />
            </NavLink>

            <img
              className={classes.logo}
              src={LogoText}
              alt="Link to the home page"
              width="109"
            />
            <Button
              className={classes.btnSearch}
              onClick={() => setShowSearch(true)}
            >
              <Icon size="25px" icon={'search'} color={'white'} />
            </Button>
          </>
        )}
      </div>
      {loader ? (
        <Loading />
      ) : (
        <div className={classes.bodyWrap}>
          <div className={classes.infoWrap}>
            <div className={classes.company}>
              <Heading className={classes.companyInfo}>
                {data.companyName}
              </Heading>

              {data.status && data.status !== '-' && data.status !== 'NA' && (
                <CompanyStatus status={data.status} addStatus={data.poc} />
              )}
            </div>
            <Text size={4} className={classes.workspace}>
              {data.workspace}
            </Text>
          </div>
          <SearchField
            placeholder={'Search User Name'}
            value={search}
            handleChangeCallback={handleSearch}
            handleClear={searchClear}
            className={classes.search}
          />
          <TableTitle
            className={classes.tableCounter}
            title={'Team Results'}
            additional={teamData.totalRecords}
          />

          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.thHeader}>
                  <Text weight={'m'} size={6} className={classes.field}>
                    Member
                  </Text>
                  <SortingIcon
                    direction={direction}
                    field={'Member'}
                    fieldName={'Member'}
                    onClick={handleSortable}
                  />
                </th>
                <th className={classes.thHeader}>
                  <Text weight={'m'} size={6} className={classes.field}>
                    Role
                  </Text>
                  <Button className={classes.btnFilter} onClick={handleRole}>
                    <Icon size="16px" icon="filter" />
                  </Button>
                </th>
                <th className={classes.thHeader}></th>
              </tr>
            </thead>
            <tbody>
              {dataForTable.length > 0 &&
                dataForTable.map(user => (
                  <tr className={classes.trBody} key={user.userid}>
                    <td className={classes.tdBody}>
                      <Member member={createMember(user)} />
                    </td>
                    <td className={classes.tdBody}>
                      <Text>{user.role}</Text>
                    </td>
                    <td className={cn(classes.tdBody, classes.tdBody_btn)}>
                      {user.hasSupportAccess && (
                        <Button
                          className={classes.btnLogin}
                          onClick={() => handleLogin(user)}
                        >
                          <Icon
                            size="20px"
                            icon={'connector'}
                            color={'white'}
                          />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!dataForTable.length && search.length > 0 && <EmptyState />}
        </div>
      )}
    </>
  );
};

export default Team;
